// Lättläge: små projekt (låg SKR-profil) behöver inte hela dokumentuppsättningen.
// Vi markerar de dokument som normalt inte är aktuella som "Ej aktuellt" i stället
// för att ta bort dem – allt går att slå tillbaka.
import { supabase } from "@/integrations/supabase/client";
import { PROCESS_STEPS } from "./constants";

export type SkrLevel = "latt" | "normal" | "komplex";

/**
 * Dokument som normalt inte är aktuella i ett litet, kort och lågriskprojekt.
 * OBS: Dok 04 Förhandsanmälan ingår INTE – den styrs av arbetets omfattning
 * (30 arbetsdagar/20 personer eller 500 persondagar), inte av riskprofilen.
 */
export const SIMPLE_MODE_DOCS = ["23", "25", "26", "02", "33"] as const;

/**
 * Vad som göms per projektnivå. Endast "lätt" gömmer något automatiskt –
 * normala och komplexa projekt behöver hela uppsättningen.
 */
export const SIMPLE_MODE_DOCS_BY_LEVEL: Record<SkrLevel, readonly string[]> = {
  latt: SIMPLE_MODE_DOCS,
  normal: [],
  komplex: [],
};

export const SIMPLE_MODE_NOTE =
  "Markerat som ej aktuellt i lättläge utifrån projektets SKR-profil. Kan slås tillbaka när som helst.";

interface SkrAnswer {
  svar?: string;
}

/** Läser projektets SKR-bedömning och klassar projektet. */
export const fetchSkrLevel = async (projectId: string): Promise<SkrLevel | null> => {
  const { data } = await supabase
    .from("skr_assessments")
    .select("data, updated_at")
    .eq("project_id", projectId)
    .is("deleted_at", null)
    .order("updated_at", { ascending: false })
    .limit(1);

  const row = (data || [])[0] as { data?: unknown } | undefined;
  const svar = (row?.data as { svar?: Record<string, SkrAnswer> } | undefined)?.svar;
  if (!svar) return null;

  const values = Object.values(svar)
    .map((s) => s?.svar)
    .filter(Boolean) as string[];
  if (values.length < 3) return null;

  const hog = values.filter((v) => v === "hög").length;
  const lag = values.filter((v) => v === "låg").length;

  if (hog >= 3) return "komplex";
  if (hog === 0 && lag >= values.length * 0.6) return "latt";
  return "normal";
};

export interface ProjectProfile {
  level: SkrLevel | null;
  /** Klartext om varför projektet inte räknas som litet. */
  reasons: string[];
}

const DAY = 24 * 60 * 60 * 1000;

/**
 * Klassar projektet utifrån SKR-profilen OCH faktiska förhållanden:
 * hur lång tid projektet pågår och om verksamhet/tredje man berörs.
 * Ett kort projekt med låga SKR-svar men pågående verksamhet är inte "lätt".
 */
export const fetchProjectProfile = async (projectId: string): Promise<ProjectProfile> => {
  const [skr, stagesRes, thirdRes, surveysRes, noticeRes] = await Promise.all([
    fetchSkrLevel(projectId),
    supabase.from("project_stages").select("start_date, end_date").eq("project_id", projectId),
    supabase.from("third_party_plans").select("id").eq("project_id", projectId).limit(1),
    supabase.from("work_area_surveys").select("adjacent_activity").eq("project_id", projectId),
    supabase.from("prior_notifications").select("id").eq("project_id", projectId).limit(1),
  ]);

  const reasons: string[] = [];
  let level = skr;

  const dates = ((stagesRes.data || []) as Array<{ start_date: string | null; end_date: string | null }>)
    .flatMap((s) => [s.start_date, s.end_date])
    .filter(Boolean) as string[];
  if (dates.length >= 2) {
    const sorted = [...dates].sort();
    const days = Math.round(
      (new Date(sorted[sorted.length - 1]).getTime() - new Date(sorted[0]).getTime()) / DAY
    );
    if (days > 120) {
      reasons.push(`Projektet pågår i ca ${days} dagar – för långt för lättläge.`);
      if (level === "latt") level = "normal";
    }
  }

  if ((thirdRes.data || []).length > 0) {
    reasons.push("Det finns en plan för tredje man – samordning behövs löpande.");
    if (level === "latt") level = "normal";
  }

  const ongoing = ((surveysRes.data || []) as Array<{ adjacent_activity: string | null }>).some(
    (s) => (s.adjacent_activity || "").trim().length > 0
  );
  if (ongoing) {
    reasons.push("Verksamhet pågår intill arbetsområdet enligt arbetsområdesutredningen.");
    if (level === "latt") level = "normal";
  }

  if ((noticeRes.data || []).length > 0) {
    reasons.push("Förhandsanmälan finns – arbetet är av den omfattning som kräver full uppsättning.");
    if (level === "latt") level = "normal";
  }

  return { level, reasons };
};



/** Sätter (eller återställer) status "not_applicable" för lättlägets dokument. */
export const applySimpleMode = async (projectId: string, enable: boolean) => {
  const rows = SIMPLE_MODE_DOCS.map((doc) => {
    const def = PROCESS_STEPS.find((s) => s.docNumber === doc);
    if (!def) return null;
    return {
      project_id: projectId,
      doc_number: doc,
      title: def.title,
      phase: def.phase,
      description: def.description,
      app_link: def.appLink ?? null,
      status: enable ? "not_applicable" : "not_started",
      not_applicable: enable,
      not_applicable_reason: enable ? SIMPLE_MODE_NOTE : null,
      sort_order: PROCESS_STEPS.indexOf(def),
    };
  }).filter(Boolean) as Record<string, unknown>[];

  const { error } = await supabase
    .from("process_steps")
    .upsert(rows as never, { onConflict: "project_id,doc_number" });
  if (error) throw new Error(error.message);
};

/**
 * Enbart projektering: uppdrag som slutar vid överlämning av handlingar.
 * Utförande- och avslutsdokumenten markeras som ej aktuella så att
 * färdigställandegraden speglar det uppdrag som faktiskt ska utföras.
 * Dok 31 (kvarvarande bruksrisker) behålls – den upprättas av Bas-P.
 */
export const DESIGN_ONLY_KEPT_DOCS = ["31"];

export const DESIGN_ONLY_DOCS = PROCESS_STEPS.filter(
  (s) =>
    (s.phase === "utforande" || s.phase === "avslut") &&
    !DESIGN_ONLY_KEPT_DOCS.includes(s.docNumber)
).map((s) => s.docNumber);

export const DESIGN_ONLY_NOTE =
  "Ej aktuellt – projektet är registrerat som enbart projektering. Kan slås tillbaka när som helst.";

/** Sätter (eller återställer) status för utförande- och avslutsdokument. */
export const applyDesignOnlyMode = async (projectId: string, enable: boolean) => {
  const rows = DESIGN_ONLY_DOCS.map((doc) => {
    const def = PROCESS_STEPS.find((s) => s.docNumber === doc);
    if (!def) return null;
    return {
      project_id: projectId,
      doc_number: doc,
      title: def.title,
      phase: def.phase,
      description: def.description,
      app_link: def.appLink ?? null,
      status: enable ? "not_applicable" : "not_started",
      not_applicable: enable,
      not_applicable_reason: enable ? DESIGN_ONLY_NOTE : null,
      sort_order: PROCESS_STEPS.indexOf(def),
    };
  }).filter(Boolean) as Record<string, unknown>[];

  const { error } = await supabase
    .from("process_steps")
    .upsert(rows as never, { onConflict: "project_id,doc_number" });
  if (error) throw new Error(error.message);
};

/** Läser projektets omfattningsläge: "full" eller "design_only". */
export const fetchScopeMode = async (projectId: string): Promise<string> => {
  const { data } = await supabase
    .from("projects")
    .select("scope_mode")
    .eq("id", projectId)
    .maybeSingle();
  return (data as { scope_mode?: string } | null)?.scope_mode || "full";
};

/** Sparar omfattningsläget och uppdaterar berörda processteg. */
export const setScopeMode = async (projectId: string, mode: string) => {
  const { error } = await supabase
    .from("projects")
    .update({ scope_mode: mode })
    .eq("id", projectId);
  if (error) throw new Error(error.message);
  await applyDesignOnlyMode(projectId, mode === "design_only");
};

export interface InapplicableDoc {
  docNumber: string;
  reason: string | null;
}

/** Läser vilka processteg som är markerade som "ej aktuella" i projektet. */
export const fetchInapplicableDocs = async (projectId: string): Promise<InapplicableDoc[]> => {
  const { data } = await supabase
    .from("process_steps")
    .select("doc_number, status, not_applicable_reason")
    .eq("project_id", projectId)
    .eq("status", "not_applicable");

  return ((data || []) as Array<{ doc_number: string; not_applicable_reason: string | null }>).map(
    (r) => ({ docNumber: r.doc_number, reason: r.not_applicable_reason })
  );
};

/** Gör ett enskilt steg aktuellt igen (eller markerar det som ej aktuellt). */
export const setStepApplicable = async (
  projectId: string,
  docNumber: string,
  applicable: boolean,
  reason?: string
) => {
  const def = PROCESS_STEPS.find((s) => s.docNumber === docNumber);
  const { error } = await supabase.from("process_steps").upsert(
    {
      project_id: projectId,
      doc_number: docNumber,
      title: def?.title ?? docNumber,
      phase: def?.phase ?? "planering",
      description: def?.description ?? null,
      app_link: def?.appLink ?? null,
      status: applicable ? "not_started" : "not_applicable",
      not_applicable: !applicable,
      not_applicable_reason: applicable ? null : reason ?? SIMPLE_MODE_NOTE,
      sort_order: def ? PROCESS_STEPS.indexOf(def) : 0,
    } as never,
    { onConflict: "project_id,doc_number" }
  );
  if (error) throw new Error(error.message);
};

/** Vilken flik i Bas-processen ett dokument hör till (om någon). */
export const docTabMap = (): Record<string, string> => {
  const map: Record<string, string> = {};
  PROCESS_STEPS.forEach((s) => {
    if (s.appLink) map[s.docNumber] = s.appLink;
  });
  return map;
};
